import { take, filter, distinctUntilChanged, map } from "rxjs/operators";
import { combineLatest } from "rxjs";
import { NgModule, forwardRef, Inject, APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, Injectable } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import { RouterModule } from "@angular/router";
import { StoreModule, Store, select } from "@ngrx/store";
import { FlexLayoutModule } from "@angular/flex-layout";
import { EffectsModule } from "@ngrx/effects";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { CmxWebComponentsModule } from "@cmx-web-components/angular";
import { TranslationService } from "@cemex-core/angular-localization-v7";
import { LoaderTags } from "./../cemexng2components/components/cmx-loader";
import { AppComponent } from "./components/app/app.component";
import { ProjectConstants, EndpointsConstants } from "./../cemexcomponents/constants/v2/";
import { AppRoutingModule } from "./app.routing";
import { LegalEntityDataService } from "./services/legalEntityData.service";
import { SingletonsModule, WINDOW_TOKEN, ENVIRONMENT_TOKEN, APP_VERSION_TOKEN } from "./singletons.module";
import { ResponseInterceptorFactory } from "./interceptors/response-interceptorFactory";
// FIXME: Implementar los breakpoints que indica la guía de diseño
// import { CustomBreakpointsModule } from './configuration/breakpoints.module';
import { CoreModule } from "./core/core.module";
import { IpcFeatureToggleModule } from "../cemexng2components/components/feature-toggle/feature-toggle.module";
import { IpcFeatureToggleService } from "../cemexng2components/components/feature-toggle/feature-toggle.service";
import { IpcFeatureToggleConfiguration } from "../cemexng2components/components/feature-toggle/feature-toggle.configuration";
import * as RolloutContainers from "../rollout-containers";
import { RolesService } from "./services";
import * as fromCore from "./core/reducers/";
import * as features from "@features/index";

import { HttpCemex, SessionService, CountlyService } from "@cemex-core/angular-services-v8";
import { AppConfigurationModule } from "../cemexng2components/components/app-configuration/ipc-app-configuration.module";
import { RouterEffects } from "./core/effects/router.effect";
import { PaymentsCoreModule } from "./payments/payments-core.module";
import { Broadcaster } from "@cemex-core/events-v7";
import { FinancialDocumentsRouterModule } from "./financial-documents-router/financial-documents-router.module";
import { CmxCookiebotModule } from "@cemex/cmx-cookiebot-v7";
import { ResponseInterceptorService } from "./interceptors/response-interceptor.service";
import {
  IamAuthService,
  IamBaseService,
  IamSettingsService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MSALGuardConfigFactory,
  MsalGuardFromService,
  MSALInstanceFactory,
  MsalInterceptor,
  MSALInterceptorConfigFactory,
  MsalRedirectComponent,
  MsalService,
} from "@cemex-core/msal-services-v1";

@Injectable()
export class LoaderTagsFactory {
  constructor(@Inject(forwardRef(() => TranslationService)) translation: TranslationService) {
    return {
      please_wait: translation.pt("views.loader.please_wait"),
      taking_longer_than_expected: translation.pt("views.loader.longer_than_expected"),
      error: translation.pt("views.loader.error"),
      no_connection: "No internet connection detected",
    } as LoaderTags;
  }
}

@Injectable()
export class DatepickerTagsFactory {
  constructor(@Inject(forwardRef(() => TranslationService)) translation: TranslationService) {
    return {
      cancel: translation.pt("views.global.btn_cancel"),
      apply: translation.pt("views.invoices_table.apply"),
    };
  }
}

@Injectable()
export class DatepickerInputTagsFactory {
  constructor(@Inject(forwardRef(() => TranslationService)) translation: TranslationService) {
    return {
      placeholder: translation.pt("select_date"),
      cancel: translation.pt("views.global.btn_cancel"),
      apply: translation.pt("views.invoices_table.apply"),
    };
  }
}

export const RTLFactory = (translation: TranslationService) => {
  return translation.selectedLanguage.rtl;
};
// export class DatepickerStartDayFactory {
//   constructor(@Inject(forwardRef(() => TranslationService)) translation: TranslationService) {
//     return translation.selectedLanguage.firstWeekDay;
//   }
// }

export const sessionServiceFactory = (http: HttpCemex, iamService: IamAuthService) => {
  return new SessionService(http, new Broadcaster(), "v6", iamService);
};

export const CwcDesignVersionFactory = () => {
  return features.core.cwc_design_version.getValue();
};

export const RolloutSetupFactory = (service: IpcFeatureToggleService) => {
  return () => service.setup();
};

export class RolloutConfigurationClass {
  constructor(
    @Inject(forwardRef(() => Store)) private store$: Store<fromCore.State>,
    @Inject(forwardRef(() => RolesService)) private rolesService: RolesService,
    @Inject(forwardRef(() => SessionService)) private sessionService: SessionService,
    @Inject(WINDOW_TOKEN) private window: any,
    @Inject(ENVIRONMENT_TOKEN) private environment: string,
    @Inject(APP_VERSION_TOKEN) private appVersion: string
  ) {
    // console.log("Injected Window", this.window);
    // console.log("Injected Environment", this.environment);
    const customProperties: Map<string, any> = new Map();

    const country$ = combineLatest(
      this.store$.pipe(
        select(fromCore.getLegalEntity),
        filter((legalEntity$) => legalEntity$?.countryCode !== null && legalEntity$?.countryCode !== undefined),
        distinctUntilChanged(),
        map((legalEntity$) => legalEntity$.countryCode.trim())
      )
    )
      .pipe(take(1))
      .pipe(map(([x]) => x));

    const roles$ = this.sessionService.currentLegalEntity.pipe(distinctUntilChanged()).pipe(
      map((x) => {
        return this.sessionService.isLoggedIn
          ? this.rolesService
              .getAllUserRoles()
              .map((r) => r.roleCode)
              .join(",")
          : "";
      })
    );

    // TODO: move to change legalentity reducer
    // const customer$ = this.sessionService.currentLegalEntity
    // .distinctUntilChanged()
    // .map(x => {
    //     return this.sessionService. isLoggedIn ? this.sessionService.customer ? this.sessionService.customer.customerCode : '' : '';
    // });
    const customer$ = combineLatest(
      this.store$.pipe(
        select(fromCore.getLegalEntity),
        filter(
          (legalEntity$) =>
            legalEntity$?.legalEntityTypeCode !== null && legalEntity$?.legalEntityTypeCode !== undefined
        ),
        distinctUntilChanged(),
        map((legalEntity$) => legalEntity$.legalEntityTypeCode.trim())
      )
    )
      .pipe(take(1))
      .pipe(map(([x]) => x));

    const userProfile$ = combineLatest(
      this.store$.pipe(
        select(fromCore.getLegalEntity),
        filter((legalEntity$) => legalEntity$?.countryCode !== null && legalEntity$?.countryCode !== undefined),
        distinctUntilChanged(),
        map(() => {
          return this.sessionService.isLoggedIn
            ? this.sessionService.userProfile
              ? this.sessionService.userProfile.userAccount
              : ""
            : "";
        })
      )
    )
      .pipe(take(1))
      .pipe(map(([x]) => x));

    // const userProfile$ = this.sessionService.currentLegalEntity
    // .distinctUntilChanged()
    // .map(x => {
    //     return this.sessionService. isLoggedIn ?
    //         this.sessionService.userProfile ?
    //         this.sessionService.userProfile.userAccount : '' : '';
    // });

    const userType$ = combineLatest(
      this.store$.pipe(
        select(fromCore.getLegalEntity),
        filter((legalEntity$) => legalEntity$?.countryCode !== null && legalEntity$?.countryCode !== undefined),
        distinctUntilChanged(),
        map(() => {
          return this.sessionService.isLoggedIn
            ? this.sessionService.userProfile
              ? this.sessionService.userProfile.userType
              : ""
            : "";
        })
      )
    )
      .pipe(take(1))
      .pipe(map(([x]) => x));

    customProperties.set("country", country$);
    customProperties.set("role", roles$);
    customProperties.set("customer", customer$);
    customProperties.set("user", userProfile$);
    customProperties.set("version", this.appVersion);
    customProperties.set("userType", userType$);
    customProperties.set("domain", window.location.hostname);

    return {
      customProperties: customProperties,
      containers: RolloutContainers.containers,
      key: this.window["ROLLOUT"],
      environment: this.environment,
      // window: this.window
    } as IpcFeatureToggleConfiguration as any;
  }
}

@NgModule({
  bootstrap: [AppComponent, MsalRedirectComponent],
  declarations: [AppComponent],
  imports: [
    CmxCookiebotModule.forRoot({ widgetEnabled: true }),
    BrowserModule,
    // tslint: disable-next-line
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    // FIXME: Implementar los breakpoints que dicta la guía de diseño. Con la actualización a NG7 ya debería ser posible.
    // CustomBreakpointsModule,
    // FlexLayoutModule.withConfig({disableDefaultBps: true}),
    FlexLayoutModule,
    AppRoutingModule,
    CmxWebComponentsModule.forRoot(),
    StoreModule.forRoot({}),
    EffectsModule.forRoot([RouterEffects]),
    CoreModule,
    PaymentsCoreModule,
    FinancialDocumentsRouterModule,
    SingletonsModule.forRoot(),
    /**
     * Store devtools instrument the store retaining past versions of state
     * and recalculating new states. This enables powerful time-travel
     * debugging.
     *
     * To use the debugger, install the Redux Devtools extension for either
     * Chrome or Firefox
     *
     * See: https://github.com/zalmoxisus/redux-devtools-extension
     */
    StoreDevtoolsModule.instrument({
      name: "Invoices, Payments, and Credits",
      logOnly: true,
    }),
    IpcFeatureToggleModule,
    AppConfigurationModule,
  ],
  providers: [
    ProjectConstants,
    EndpointsConstants,
    HttpCemex,
    LegalEntityDataService,
    CountlyService,
    {
      provide: SessionService,
      deps: [HttpCemex, IamAuthService],
      useFactory: sessionServiceFactory,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: RolloutSetupFactory,
      multi: true,
      deps: [IpcFeatureToggleService],
    },
    MsalService,
    MsalGuardFromService,
    MsalBroadcastService,
    IamSettingsService,
    IamBaseService,
    {
      provide: IamAuthService,
      deps: [HttpCemex, MsalService, MsalBroadcastService],
      useClass: IamAuthService,
    },
    {
      provide: MSAL_INSTANCE,
      deps: [IamSettingsService],
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    MsalInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: (factory: ResponseInterceptorFactory) => factory.createInterceptor(),
      deps: [ResponseInterceptorFactory],
      multi: true,
    },
    ResponseInterceptorService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ResponseInterceptorService,
      multi: true,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      deps: [IamSettingsService],
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: "ROLLOUT_CONFIGURATION",
      useClass: RolloutConfigurationClass,
      deps: [Store, RolesService, SessionService, WINDOW_TOKEN, ENVIRONMENT_TOKEN, APP_VERSION_TOKEN],
    },
    { provide: "TRANSLATION_PRODUCT_PATH", useValue: "invoices-payments" },
    { provide: "TRANSLATION_LANGUAGES", useValue: window["CMX_LANGUAGES"] },
    { provide: "DEFAULT_LANGUAGE_ISO", useValue: "en-US" },
    {
      provide: "RTL",
      deps: [TranslationService],
      useFactory: RTLFactory,
    },
    { provide: "CWC_DESIGN_VERSION", useFactory: CwcDesignVersionFactory },
    // FIXME: Are these needed after the update to CWC?
    // { provide: "CMX_DATEPICKER_WEEK_FIRST_DAY", useFactory: DatepickerStartDayFactory },
    { provide: "CMX_DATEPICKER_TAGS", useClass: DatepickerTagsFactory },
    { provide: "CMX_DATEPICKER_INPUT_TAGS", useClass: DatepickerInputTagsFactory },
    { provide: "LoaderTags", useClass: LoaderTagsFactory }
  ],
  exports: [],
  entryComponents: [],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor() {
    //
  }
}
